/**
 * Our own analytics (the Analytics section of the Admin), sent to the
 * track-analytics edge function. One sender for page views and events.
 *
 * Every real visitor is counted, with or without cookie consent:
 *
 *   consent given    a random visitor id kept in this browser (visitor-id.ts),
 *                    so returning visitors and sign-ups can be linked.
 *   no consent       nothing is written to the device and no id is sent. The
 *                    server recognises the visitor for one day only, by a hash
 *                    salted with a secret that is thrown away after the day
 *                    (see track-analytics), and keeps no user id, IP or user
 *                    agent for these events.
 *
 * Bots, previews and internal devices are never counted (same gate as GTM),
 * and neither are admins. Nothing is sent before the visitor has shown they
 * are a person (a key, click, tap, wheel or real mouse move, see index.html):
 * events wait for that and are dropped if the page closes first.
 */
import { getVisitorId, getSessionId } from "@/lib/visitor-id";
import { shouldTrack, checkIsAdmin } from "@/lib/analytics-tracker";
import { hasAnalyticsConsent } from "@/lib/consent";
import { getAttribution, isFlowReferrer } from "@/lib/attribution";
import { geolocationFromTimezone } from "@/contexts/GeolocationContext";
import { whenHuman } from "@/lib/gtm";
import { supabase } from "@/integrations/supabase/client";

/** Event params worth keeping: short scalars only, no nested objects. */
function cleanParams(params: Record<string, unknown>): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || k === "event") continue;
    if (typeof v === "string") out[k] = v.slice(0, 200);
    else if (typeof v === "number" || typeof v === "boolean") out[k] = v;
  }
  return out;
}

let tzCountry: string | null | undefined;
function country(): string | null {
  if (tzCountry === undefined) tzCountry = geolocationFromTimezone().country_code || null;
  return tzCountry;
}

async function accessToken(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

export interface OwnEventOptions {
  /** Defaults to the current path. */
  pagePath?: string;
  pageTitle?: string;
  /** Page views carry the campaign and the referrer. */
  withSource?: boolean;
}

export async function sendOwnEvent(
  eventType: string,
  params: Record<string, unknown> = {},
  opts: OwnEventOptions = {},
): Promise<void> {
  if (!shouldTrack()) return;
  if (await checkIsAdmin()) return;

  const consented = hasAnalyticsConsent();
  const body: Record<string, unknown> = {
    page_path: opts.pagePath ?? window.location.pathname + window.location.search,
    page_title: opts.pageTitle ?? document.title,
    event_type: eventType,
    event_data: cleanParams(params),
    consent: consented ? "granted" : "denied",
    tz_country: country(),
  };

  if (opts.withSource) {
    const last = getAttribution().last;
    if (last && last.source !== "(direct)") {
      Object.assign(body, {
        utm_source: last.source,
        utm_medium: last.medium,
        utm_campaign: last.campaign,
      });
    }
    if (document.referrer && !isFlowReferrer(document.referrer)) body.referrer = document.referrer;
  }

  // The page and source above are read now; the send waits for a person.
  await whenHuman();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  };
  if (consented) {
    // Only with consent: a lasting id for this browser and the account link.
    body.visitor_id = getVisitorId();
    body.session_id = getSessionId();
    const token = await accessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-analytics`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {
    /* best effort */
  });
}

/** Links this browser's visitor id to the signed-in account (consent only). */
export async function identifyOwnVisitor(accessTokenValue: string): Promise<void> {
  if (!shouldTrack() || !hasAnalyticsConsent()) return;
  if (await checkIsAdmin()) return;
  fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-analytics`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${accessTokenValue}`,
    },
    body: JSON.stringify({
      page_path: window.location.pathname,
      page_title: document.title,
      event_type: "identify",
      consent: "granted",
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
    }),
    keepalive: true,
  }).catch(() => {
    /* best effort */
  });
}
